import throttle from 'lodash/throttle';
import {
  MouseEvent,
  ReactNode,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../../../common/shared/utils/constants';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {LongTap} from '../../../../../../design-system/atoms/LongTap/LongTap';
import {PC_PULL_COMPONENT_HEIGHT} from '../constant/talkViewerConstant';
import {events} from '../constant/talkViewerEvents';
import {TalkViewerEventHandleBottomPull} from '../presentational/TalkViewerEventHandleBottomPull';
import {TalkViewerEventHandleTopPull} from '../presentational/TalkViewerEventHandleTopPull';
import {ProductKey, SceneKey} from '../type/KeyType.type';
import {ViewingMode} from '../type/ViewingMode.enum';
import {disableScroll} from '../util/disableScroll';
import {enableScroll} from '../util/enableScroll';

interface Props {
  children: ReactNode;
  productId: ProductKey;
  sceneId?: SceneKey;
}

export function TalkViewerEventHandleContainer({
  children,
  productId,
  sceneId,
}: Props) {
  const {isMobile} = requestContext.requestInfo.display;
  const {
    autoTap,
    sceneMap,
    isViewerEndExposed,
    sceneInfoMap,
    viewingMode,
    fullscreen,
    productInfoMap,
    isPreview,
    isTutorialExposed,
    topViewerMenuHeight,
    isDialogExposed,
    isHandlingSeekBar,
  } = useTypedSelector(state => ({
    autoTap: state.talkViewer.internalSettings.autoTap,
    sceneMap: state.talkViewer.sceneMap,
    productInfoMap: state.talkViewer.productInfoMap,
    isViewerEndExposed: state.viewerEnd.isExposed,
    sceneInfoMap: state.talkViewer.sceneInfoMap,
    viewingMode: state.talkViewer.settings.viewingMode,
    fullscreen: state.talkViewer.fullscreen,
    isPreview: state.talkViewer.isPreview,
    isTutorialExposed: state.talkViewer.isTutorialExposed,
    topViewerMenuHeight: state.talkViewer.settings.viewerMenuHeight.top,
    isDialogExposed: false,
    isHandlingSeekBar: state.talkViewer.isHandlingSeekBar,
  }));

  const ref = useRef<HTMLDivElement | null>(null);
  const isTouchStarted = useRef<boolean>(false);
  const startPos = useRef<{x: number; y: number}>({x: 0, y: 0});
  const [topRefreshHeight, setTopRefreshHeight] = useState(0);
  const [bottomRefreshHeight, setBottomRefreshHeight] = useState(0);
  const isEndOfTopScroll = useRef(false);
  const isEndOfBottomScroll = useRef(false);
  const pcScrollBlockTimer = useRef(0);

  const dispatch = useTypedDispatch();
  const viewerSwipedUp = useMemo(
    () =>
      throttle(() => dispatch(events.viewerSwipedUp()), 1000, {
        trailing: false,
      }),
    [dispatch]
  );
  const viewerSwipedDown = useMemo(
    () =>
      throttle(() => dispatch(events.viewerSwipedDown()), 1000, {
        trailing: false,
      }),
    [dispatch]
  );

  useEffect(() => {
    return () => window.clearTimeout(pcScrollBlockTimer.current);
  }, []);

  /**
   * PC 버전용(개발 테스트 용도) 뷰어메뉴 on/off를 위한 Wheel 이벤트 핸들러.
   * 톡뷰어는 현재 mobile, webview 만 지원하기 때문에 PC 이벤트에 대한 퀄리티는 크게 신경쓰지 않아도 됨.
   * hook 을 사용하면 렌더링이 너무 빈번해져 컴포넌트 내 리스너로 구현함
   */
  useEffect(() => {
    if (isMobile) {
      return;
    }
    const windowWheelListener = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        dispatch(events.viewerSwipedDown());
      } else {
        dispatch(events.viewerSwipedUp());
      }
    };
    window.addEventListener('wheel', windowWheelListener);
    return () => {
      window.removeEventListener('wheel', windowWheelListener);
    };
  }, [dispatch, autoTap, isMobile]);

  // PC버전에서 씬 이동 컴포넌트를 컨트롤하기 위한 코드
  useEffect(() => {
    function scrollHandler() {
      if (isMobile) {
        return;
      }
      const _currentSceneId = productInfoMap[productId]?.currentSceneId;
      if (!_currentSceneId) {
        return;
      }

      const _currentSceneInfo = sceneInfoMap[_currentSceneId];
      if (!_currentSceneInfo) {
        return;
      }
      if (
        topRefreshHeight !== 0 &&
        window.scrollY > PC_PULL_COMPONENT_HEIGHT + 50
      ) {
        setTopRefreshHeight(0);
      }
      if (
        bottomRefreshHeight !== 0 &&
        document.body.scrollHeight - window.scrollY - window.innerHeight >
          PC_PULL_COMPONENT_HEIGHT + 50
      ) {
        setBottomRefreshHeight(0);
      }
      const _scene = sceneMap[_currentSceneInfo.sceneId];
      const isTopPc = window.scrollY === 0;
      const isBottomPc =
        window.scrollY + window.innerHeight + 10 >= document.body.scrollHeight;

      if (
        isBottomPc &&
        viewingMode === ViewingMode.Scroll &&
        !isViewerEndExposed
      ) {
        if (bottomRefreshHeight === 0) {
          // PC미리보기에서는 뷰어엔드로 갈 일이 없으므로 다음씬이 없으면 아무 동작하지 않는다
          if (!_scene.nextSceneId && isPreview) {
            return;
          }
          setBottomRefreshHeight(PC_PULL_COMPONENT_HEIGHT);
          disableScroll();
          pcScrollBlockTimer.current = window.setTimeout(
            () => enableScroll(),
            500
          );
        } else {
          dispatch(events.viewerPulledUp());
          window.scrollTo(0, 1);
          setTopRefreshHeight(0);
          setBottomRefreshHeight(0);
        }
      }

      if (isTopPc && (_scene.prevSceneId || isViewerEndExposed)) {
        if (topRefreshHeight === 0) {
          setTopRefreshHeight(PC_PULL_COMPONENT_HEIGHT);
          window.scrollTo(0, PC_PULL_COMPONENT_HEIGHT);
          disableScroll();
          pcScrollBlockTimer.current = window.setTimeout(
            () => enableScroll(),
            500
          );
        } else {
          dispatch(events.viewerPulledDown());
          setTopRefreshHeight(0);
          setBottomRefreshHeight(0);
        }
      }
    }
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, [
    bottomRefreshHeight,
    dispatch,
    productId,
    productInfoMap,
    sceneInfoMap,
    sceneMap,
    viewingMode,
    topRefreshHeight,
    isMobile,
    isPreview,
    isViewerEndExposed,
  ]);

  // 씬에 변화가 있으면 PC버전의 Pull component를 초기화한다.
  useEffect(() => {
    if (isMobile) {
      return;
    }
    setTopRefreshHeight(0);
    setBottomRefreshHeight(0);
  }, [sceneInfoMap, isMobile]);

  const handleTouchMove = useCallback(
    (currPos: {x: number; y: number}) => {
      if (currPos.y === 0) {
        setTopRefreshHeight(0);
        setBottomRefreshHeight(0);
        return;
      }
      // Tap 모드인 경우 다음 씬 이동이 탭으로 되기도 하고, 현재 이 Scene의 마지막 Talk까지 노출되었는지 지표가 또 필요하므로 우선 막아둠
      const isShowBottomRefresh =
        isEndOfBottomScroll.current &&
        viewingMode === ViewingMode.Scroll &&
        !isViewerEndExposed;

      const isShowTopRefresh = isEndOfTopScroll.current;
      if (isShowBottomRefresh) {
        const height =
          currPos.y < startPos.current.y ? startPos.current.y - currPos.y : 0;
        setBottomRefreshHeight(height);
      }
      if (isShowTopRefresh) {
        const height =
          currPos.y > startPos.current.y ? currPos.y - startPos.current.y : 0;
        setTopRefreshHeight(height);
      }

      const isSwipedDown = currPos.y > startPos.current.y;
      if (isSwipedDown) {
        viewerSwipedDown();
      } else {
        viewerSwipedUp();
      }
    },
    [viewingMode, isViewerEndExposed, viewerSwipedDown, viewerSwipedUp]
  );

  /**
   * Move 시점을 잡기 위한 목적
   */
  const onTouchStartCapture = useCallback(
    (e: TouchEvent) => {
      if (
        fullscreen ||
        isTutorialExposed ||
        isDialogExposed ||
        isHandlingSeekBar
      ) {
        return;
      }
      startPos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      isTouchStarted.current = true;
      isEndOfTopScroll.current = getIsWindowTop(10);
      isEndOfBottomScroll.current = getIsWindowBottom(10);
    },
    [fullscreen, isTutorialExposed, isDialogExposed, isHandlingSeekBar]
  );

  const onTouchMoveCapture = useCallback(
    (e: TouchEvent) => {
      if (
        fullscreen ||
        isTutorialExposed ||
        isDialogExposed ||
        isHandlingSeekBar
      ) {
        return;
      }
      if (isTouchStarted.current) {
        handleTouchMove({
          x: e.touches?.[0]?.clientX,
          y: e.touches?.[0]?.clientY,
        });
      }
    },
    [
      fullscreen,
      isTutorialExposed,
      isDialogExposed,
      isHandlingSeekBar,
      handleTouchMove,
    ]
  );

  const onTouchEnd = useCallback(() => {
    if (
      fullscreen ||
      isTutorialExposed ||
      isDialogExposed ||
      isHandlingSeekBar
    ) {
      return;
    }
    isTouchStarted.current = false;
    handleTouchMove({x: 0, y: 0});
  }, [
    fullscreen,
    isTutorialExposed,
    isDialogExposed,
    isHandlingSeekBar,
    handleTouchMove,
  ]);

  const onLongTap = useCallback(() => {
    dispatch(events.backgroundLongTapPressed());
  }, [dispatch]);
  const onClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      dispatch(events.backgroundTapPressed());
    },
    [dispatch]
  );
  const onViewerPulledDown = useCallback(() => {
    dispatch(events.viewerPulledDown());
  }, [dispatch]);
  const onViewerPulledUp = useCallback(() => {
    dispatch(events.viewerPulledUp());
  }, [dispatch]);
  // 씬정보가 없다면 비정상적인 상황이므로 children을 그대로 리턴해줍니다.
  if (!sceneId) {
    return null;
  }
  const scene = sceneMap[sceneId];
  return (
    <LongTap
      ref={ref}
      onTouchStartCapture={onTouchStartCapture}
      onTouchMoveCapture={onTouchMoveCapture}
      onTouchEndCapture={onTouchEnd}
      onLongTap={onLongTap}
      onClick={onClick}
      isMobile={isMobile}
      className="relative mx-auto w-full"
    >
      <TalkViewerEventHandleTopPull
        prevSceneId={scene.prevSceneId}
        height={topRefreshHeight}
        isMobile={isMobile}
        topViewerMenuHeight={topViewerMenuHeight}
        isViewerEndExposed={isViewerEndExposed}
        onViewerPulledDown={onViewerPulledDown}
      />
      {children}
      <TalkViewerEventHandleBottomPull
        nextSceneId={scene.nextSceneId}
        height={bottomRefreshHeight}
        isMobile={isMobile}
        isPreview={isPreview}
        onViewerPulledUp={onViewerPulledUp}
      />
    </LongTap>
  );
}

function getIsWindowBottom(bottomArea = 0) {
  if (!IS_CLIENT_SIDE) {
    return false;
  }
  return (
    document.documentElement.scrollHeight -
      ((window.scrollY || window.pageYOffset) + window.innerHeight) <=
    bottomArea
  );
}

function getIsWindowTop(topArea = 0) {
  if (!IS_CLIENT_SIDE) {
    return false;
  }
  return window.scrollY <= 1 + topArea;
}
